import LeafPinLevelName, { mapGarLevelToNonGar } from "../../../../../common/leaf/LeafPinLevelName/LeafPinLevelName";
import { useAppSelector, useAppDispatch } from "../../../../../store";
import { LegsProgress, type LegsProgressItem } from "../../PerformanceTile/LegsProgress/LegsProgress";
import { PerformanceTile } from "../../PerformanceTile/PerformanceTile";
import { PerformanceTileHeader } from "../../PerformanceTile/PerformanceTileHeader/PerformanceTileHeader";
import type { HatGarTrackingResponse } from "../../../../../services/AnnualIncomeDashboard/annualIncomeDashboardAPI.types";
import { maskHatGarValue } from "./HatLegsTrackingTile.mask";
import defaultContent from "../../../../../content/performanceIncomeDashboard";
import hatContent from "../../../../../content/hat";
import { useTranslation } from "react-i18next";
import LeafRectangleLoader from "../../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { ROUTE_PATHS } from "../../../../../common/enums/routing-enums";
import { useState, useEffect } from "react";
import { HAT_ROUTES } from "../../../../../routing/SubRoutes/Hat";
import { setGlobalAwardPeriods } from "../../../../../reducers/higherAwardTracking";
import { LeafModal } from "../../../../../common/leaf/LeafModal/LeafModal";
import GARMatrix from "../../../../HigherAwardTracking/GARMatrix/GARMatrix";
import {
  getLongPeriodFormat,
  getPerformanceYearShort,
  getSelectedPerformanceYearData,
} from "../../../../../common/util/period";
import { storedLocale } from "../../../../Authentication/util";
import type { GARMatrixRow } from "../../../../../services/HigherAwardTracking/HigherAwardTrackingAPI.types";
import { AwardProgressBadge } from "../../../../../common/components/AwardProgressBadge/AwardProgressBadge";
import { parseStatus, spreadTrend } from "../utils";
import { ActionCardError } from "../../../GroupManagementDashboard/ActionCard/ActionCardError/ActionCardError";
import { trackGlobalAwardTrackingLevel } from "../../../../../common/tealium/PerformanceIncomeDashboard/PerformanceIncomeDashboardTrack";
import { IconInfoCircle } from "../../../../../common/icons/IconInfoCircle/IconInfoCircle";
import { IconCheckCircle } from "../../../../../common/icons/IconCheckCircle/IconCheckCircle";
import { IconErrorCircle } from "../../../../../common/icons/IconErrorCircle/IconErrorCircle";
import { IconWarningTriangle } from "../../../../../common/icons/IconWarningTriangle/IconWarningTriangle";
import { IconDownloadButton } from "../../../../../common/icons/IconDownloadButton/IconDownloadButton";
import { Button } from "../../../../../common/components/Button/Button";
import { MARKET_CODES } from "../../../../../common/enums/market-enums";
import useWindowSize from "../../../../../common/hooks/useWindowSize";
import breakpoints from "../../../../../common/scss/breakpoints.module.scss";
import "./HatLegsTrackingTile.scss";

interface HatLegsTrackingTileProps {
  hatGarData: HatGarTrackingResponse;
  isLoading: boolean;
  errorStatus: boolean;
  refetch: () => void;
}

const hatTileLoader = () => {
  return (
    <PerformanceTile>
      <PerformanceTileHeader label={<LeafRectangleLoader isLoading width="8rem" height="1.5rem" margin="0" />} />
      <LegsProgress
        items={[
          {
            label: <LeafRectangleLoader isLoading width="8rem" height="1.25rem" margin="0" />,
            value: <LeafRectangleLoader isLoading width="4rem" height="1.25rem" margin="0" />,
          },
          {
            label: <LeafRectangleLoader isLoading width="10rem" height="1.25rem" margin="0" />,
            value: <LeafRectangleLoader isLoading width="4rem" height="1.25rem" margin="0" />,
          },
          {
            label: <LeafRectangleLoader isLoading width="6rem" height="1.25rem" margin="0" />,
            value: <LeafRectangleLoader isLoading width="4rem" height="1.25rem" margin="0" />,
          },
        ]}
      />
    </PerformanceTile>
  );
};

const garTileLoader = () => {
  return (
    <PerformanceTile>
      <PerformanceTileHeader label={<LeafRectangleLoader isLoading width="8rem" height="1.5rem" margin="0" />} />
      <LegsProgress
        items={[
          {
            label: <LeafRectangleLoader isLoading width="3rem" height="1.25rem" margin="0" />,
            value: <LeafRectangleLoader isLoading width="4rem" height="1.25rem" margin="0" />,
          },
          {
            label: <LeafRectangleLoader isLoading width="4rem" height="1.25rem" margin="0" />,
            value: <LeafRectangleLoader isLoading width="4rem" height="1.25rem" margin="0" />,
          },
          {
            label: <LeafRectangleLoader isLoading width="2.5rem" height="1.25rem" margin="0" />,
            value: <LeafRectangleLoader isLoading width="8rem" height="1.25rem" margin="0" />,
          },
        ]}
      />
      <hr />
      <LegsProgress
        items={[
          {
            label: <LeafRectangleLoader isLoading width="8rem" height="1.25rem" margin="0" />,
            value: <LeafRectangleLoader isLoading width="3rem" height="1.25rem" margin="0" />,
          },
          {
            label: <LeafRectangleLoader isLoading width="9rem" height="1.25rem" margin="0" />,
            value: <LeafRectangleLoader isLoading width="3rem" height="1.25rem" margin="0" />,
          },
          {
            label: <LeafRectangleLoader isLoading width="4rem" height="1.25rem" margin="0" />,
            value: <LeafRectangleLoader isLoading width="2rem" height="1.25rem" margin="0" />,
          },
        ]}
      />
    </PerformanceTile>
  );
};

export const HatLegsTrackingTile = ({ hatGarData, isLoading, errorStatus, refetch }: HatLegsTrackingTileProps) => {
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { user } = useAppSelector((state) => state.boot);
  const { hasGarLegAccess } = useAppSelector((state) => state.boot.shellConfig);
  const hideInternationalInfo = useAppSelector((state) => state.boot.configuration?.hideInternationalInfo) === true;
  const { t } = useTranslation(["performanceIncomeDashboard", "hat"]);
  const { width } = useWindowSize();
  const isMobile = Number(width) < Number(breakpoints["tablet"]);
  const [showGarMatrixModal, setShowGarMatrixModal] = useState(false);
  const [showGarInfoPanel, setShowGarInfoPanel] = useState(false);

  const showData = useAppSelector((state) => state.showData.showData);
  const hatData = hatGarData.hatTracking;
  const garData = hatGarData.garTracking;
  const isChinaAbo = user.aff === MARKET_CODES.CN;
  const dispatch = useAppDispatch();
  const locale = storedLocale();
  const garLastMonthProcessed = garData.lastMonthProcessed;

  // Pre-populate the GAR period data so /gar/leg-qc-details has it when navigated to
  // This mirrors what /hat does in HigherAwardTracking.tsx
  useEffect(() => {
    if (garData.closingPeriods && garData.closingPeriods.length > 0) {
      dispatch(setGlobalAwardPeriods({ periodList: garData.closingPeriods }));
    }
  }, [dispatch, garData.closingPeriods]);

  const foundersPlatinumLegs = garData.foundersPlatinumLegs;
  const platinumLegs = garData.platinumLegs;
  const qualCreditsMaxPerLeg = garData.qualCreditMaxPerLeg;
  const qualCreditsNotCounting = garData.qualCreditsNotCounting;
  const qualCreditsMet = garData.qualCredits.met;
  const faqDownload = garData.pdfs?.faqs || "";
  const brochureDownload = garData.pdfs?.brochure || "";

  const handleDownload = (url: string, fileName: string) => {
    const href = import.meta.env.DEV
      ? url.replace(/^https:\/\/coreplus-test\.amwayglobal\.com/, "/coreplus-proxy")
      : url;
    const link = document.createElement("a");
    link.href = href;
    link.download = fileName;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const { selectedPerformanceYear: previousPerformanceYear } = getSelectedPerformanceYearData(selectedPeriod, 1);

  const handleGarMatrixOpen = () => {
    setShowGarMatrixModal(true);
    trackGlobalAwardTrackingLevel(true);
  };
  const handleGarMatrixClose = () => {
    setShowGarMatrixModal(false);
    trackGlobalAwardTrackingLevel(false);
  };

  const handleGarInfoPanelOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowGarInfoPanel(true);
  };
  const handleGarInfoPanelClose = () => {
    setShowGarInfoPanel(false);
  };

  const garDetailItems: LegsProgressItem[] = [
    garData.globalAwardTrackingLevel && {
      label: (
        <div className="HatLegsTrackingTile__tracking-level-label">
          <span className="HatLegsTrackingTile__tracking-level-text">
            {t(
              isMobile
                ? "performance.hatgar.pinTrackingGlobalAwardRecognitionMobile"
                : "performance.hatgar.pinTrackingGlobalAwardRecognition",
              (isMobile
                ? defaultContent.performance.hatgar.pinTrackingGlobalAwardRecognitionMobile
                : defaultContent.performance.hatgar.pinTrackingGlobalAwardRecognition) as string,
            )}
          </span>
          {showData && (
            <button className="HatLegsTrackingTile__view-status-link" onClick={handleGarMatrixOpen}>
              {t("performance.hatgar.viewStatus", defaultContent.performance.hatgar.viewStatus as string)}
            </button>
          )}
        </div>
      ),
      value: showData ? (
        <LeafPinLevelName pinLevelCode={mapGarLevelToNonGar(garData.globalAwardTrackingLevel.rank)} />
      ) : (
        "--"
      ),
    },
    garData.globalAwardLevelPY && {
      label: t("performance.hatgar.garAwardLevelPY", defaultContent.performance.hatgar.garAwardLevelPY as string, {
        shortPY: getPerformanceYearShort(previousPerformanceYear),
      }),
      value: showData ? <LeafPinLevelName pinLevelCode={mapGarLevelToNonGar(garData.globalAwardLevelPY.rank)} /> : "--",
    },
    garData.highestAwardLevel && {
      label: t("performance.hatgar.highestAwardLevel", defaultContent.performance.hatgar.highestAwardLevel as string),
      value: showData ? <LeafPinLevelName pinLevelCode={mapGarLevelToNonGar(garData.highestAwardLevel.rank)} /> : "--",
    },
  ].filter(Boolean) as LegsProgressItem[];

  if (errorStatus) {
    return (
      <PerformanceTile>
        <PerformanceTileHeader
          link={`/hat/${HAT_ROUTES.LEG_TRACKING}`}
          label={t("performance.hatgar.hatTileTitle", defaultContent.performance.hatgar.hatTileTitle as string)}
        />
        <ActionCardError onRetry={refetch} />
      </PerformanceTile>
    );
  }

  return isLoading ? (
    <>
      {hatTileLoader()} {garTileLoader()}
    </>
  ) : (
    <>
      {hatGarData.showHat && (
        <PerformanceTile>
          <PerformanceTileHeader
            link={`/hat/${HAT_ROUTES.LEG_TRACKING}`}
            label={t("performance.hatgar.hatTileTitle", defaultContent.performance.hatgar.hatTileTitle as string)}
            tealiumTag="hatLegsTracking"
          />
          <LegsProgress
            items={[
              ...(hideInternationalInfo
                ? []
                : [
                    {
                      label: t(
                        "performance.hatgar.hatInMarket",
                        defaultContent.performance.hatgar.hatInMarket as string,
                      ),
                      value: maskHatGarValue(hatData.inMarketLegs.value, showData),
                      ...(showData ? spreadTrend(hatData.inMarketLegs.trend) : {}),
                    },
                  ]),
              {
                label: t(
                  "performance.hatgar.hatFoundersLegs",
                  defaultContent.performance.hatgar.hatFoundersLegs as string,
                ),
                value: maskHatGarValue(hatData.foundersLegs.value, showData),
                ...(showData ? spreadTrend(hatData.foundersLegs.trend) : {}),
              },
              {
                label: t("performance.hatgar.hatTotalLegs", defaultContent.performance.hatgar.hatTotalLegs as string),
                value: maskHatGarValue(hatData.totalLegs.value, showData),
                ...(showData ? spreadTrend(hatData.totalLegs.trend) : {}),
              },
            ]}
          />
        </PerformanceTile>
      )}
      {hatGarData.showGar && (
        <PerformanceTile>
          <PerformanceTileHeader
            link={hasGarLegAccess && foundersPlatinumLegs.count > 0 ? ROUTE_PATHS.GAR_LEG_QC_DETAILS : undefined}
            label={t("performance.hatgar.garTileTitle", defaultContent.performance.hatgar.garTileTitle as string)}
            tealiumTag="garTracking"
            subtitle={
              <p className="HatLegsTrackingTile__last-month-processed">
                {t(
                  "performance.hatgar.lastMonthProcessed",
                  defaultContent.performance.hatgar.lastMonthProcessed as string,
                )}
                :{" "}
                <span>
                  {garLastMonthProcessed && garLastMonthProcessed > 0
                    ? getLongPeriodFormat(garLastMonthProcessed.toString(), locale)
                    : "--"}
                </span>
              </p>
            }
            action={
              <Button
                className="HatLegsTrackingTile__info-button"
                onClick={handleGarInfoPanelOpen}
                aria-label={
                  t("performance.hatgar.garTileTitle", defaultContent.performance.hatgar.garTileTitle as string) +
                  " information"
                }
                id="garTrackingLandingInfoButton"
              >
                <IconInfoCircle />
              </Button>
            }
          />
          <LegsProgress
            items={
              [
                {
                  label: (
                    <span
                      className={
                        showData
                          ? `HatLegsTrackingTile__kpi-label--${foundersPlatinumLegs.count >= 6 ? "success" : "error"}`
                          : undefined
                      }
                    >
                      {isChinaAbo
                        ? t(
                            "hatGarOverviewView.chinaLabels.foundersPlatinumLegs",
                            String(hatContent["hatGarOverviewView"]["chinaLabels"]["foundersPlatinumLegs"]),
                            { ns: "hat" },
                          )
                        : t(
                            "performance.hatgar.foundersPlatinumLegs",
                            defaultContent.performance.hatgar.foundersPlatinumLegs as string,
                          )}
                    </span>
                  ),
                  value: showData ? (
                    <span
                      className={`HatLegsTrackingTile__kpi-value--${foundersPlatinumLegs.count >= 6 ? "success" : "error"}`}
                    >
                      {foundersPlatinumLegs.count}
                      {foundersPlatinumLegs.count >= 6 ? (
                        <IconCheckCircle color="green" solidCheckCircle={true} size="sm" />
                      ) : (
                        <IconErrorCircle />
                      )}
                    </span>
                  ) : (
                    "--"
                  ),
                },
                platinumLegs != null && {
                  label: isChinaAbo
                    ? t(
                        "hatGarOverviewView.chinaLabels.platinumLegs",
                        String(hatContent["hatGarOverviewView"]["chinaLabels"]["platinumLegs"]),
                        { ns: "hat" },
                      )
                    : t("performance.hatgar.platinumLegs", defaultContent.performance.hatgar.platinumLegs as string),
                  value: showData ? String(platinumLegs) : "--",
                },
                {
                  label: t(
                    "performance.hatgar.totalLegsCounting",
                    defaultContent.performance.hatgar.totalLegsCounting as string,
                  ),
                  value: maskHatGarValue(garData.totalLegsCounting.value, showData),
                  ...(showData ? spreadTrend(garData.totalLegsCounting.trend) : {}),
                },
                {
                  label: showData ? (
                    <span
                      className={`HatLegsTrackingTile__kpi-label--${qualCreditsMaxPerLeg === 0 ? "error" : qualCreditsMet ? "success" : "error"}`}
                    >
                      {isMobile
                        ? t(
                            "performance.hatgar.qualificationCreditsMaxMobile",
                            defaultContent.performance.hatgar.qualificationCreditsMaxMobile as string,
                            { qcMax: qualCreditsMaxPerLeg },
                          )
                        : t(
                            "performance.hatgar.qualificationCreditsMax",
                            defaultContent.performance.hatgar.qualificationCreditsMax as string,
                            { qcMax: qualCreditsMaxPerLeg },
                          )}
                    </span>
                  ) : (
                    t(
                      "performance.hatgar.qualificationCredits",
                      defaultContent.performance.hatgar.qualificationCredits as string,
                    )
                  ),
                  value: showData ? (
                    <span className={`HatLegsTrackingTile__kpi-value--${qualCreditsMet ? "success" : "error"}`}>
                      {garData.qualCredits.value}
                      {qualCreditsMet ? (
                        <IconCheckCircle color="green" solidCheckCircle={true} size="sm" />
                      ) : (
                        <IconErrorCircle />
                      )}
                    </span>
                  ) : (
                    maskHatGarValue(garData.qualCredits.value, showData)
                  ),
                  ...(showData ? spreadTrend(garData.qualCredits.trend) : {}),
                },
                {
                  label: (
                    <span
                      className={
                        showData
                          ? `HatLegsTrackingTile__kpi-label--${qualCreditsNotCounting.count > 0 ? "warning" : "none"}`
                          : undefined
                      }
                    >
                      {t(
                        isMobile
                          ? "performance.hatgar.qualCreditsNotCountingMobile"
                          : "performance.hatgar.qualCreditsNotCounting",
                        (isMobile
                          ? defaultContent.performance.hatgar.qualCreditsNotCountingMobile
                          : defaultContent.performance.hatgar.qualCreditsNotCounting) as string,
                      )}
                    </span>
                  ),
                  value: showData ? (
                    <span
                      className={`HatLegsTrackingTile__kpi-value--${qualCreditsNotCounting.count > 0 ? "warning" : "none"}`}
                    >
                      {qualCreditsNotCounting.count}
                      {qualCreditsNotCounting.count > 0 && <IconWarningTriangle />}
                    </span>
                  ) : (
                    "--"
                  ),
                },
                {
                  label: t(
                    "performance.hatgar.foundersDiamondStatus",
                    defaultContent.performance.hatgar.foundersDiamondStatus as string,
                  ),
                  value: showData ? (
                    <AwardProgressBadge status={parseStatus(garData.founderDiamondStatus)} hideIcon />
                  ) : (
                    "--"
                  ),
                },
              ].filter(Boolean) as LegsProgressItem[]
            }
          />
          {garDetailItems.length > 0 && (
            <>
              <hr />
              <LegsProgress onLabelClick={handleGarMatrixOpen} items={garDetailItems} />
            </>
          )}
        </PerformanceTile>
      )}
      <LeafModal
        id="GARMatrixModal"
        open={showGarMatrixModal}
        onCloseCallbackFn={handleGarMatrixClose}
        large={true}
        title={t(
          "modalGARViewYourStatus.globalAwardRecognitionStatus",
          hatContent["modalGARViewYourStatus"]["globalAwardRecognitionStatus"],
          { ns: "hat" },
        ).toString()}
        subtitle={t("modalGARViewYourStatus.py", hatContent["modalGARViewYourStatus"]["py"], {
          ns: "hat",
          performanceYear: getPerformanceYearShort(selectedPeriod),
        }).toString()}
      >
        <GARMatrix rows={hatGarData.garMatrixRows ?? ([] as GARMatrixRow[])} isChinaAbo={isChinaAbo} />
      </LeafModal>
      <LeafModal
        id="garTrackingInfoPanel"
        open={showGarInfoPanel}
        onCloseCallbackFn={handleGarInfoPanelClose}
        title={t("performance.hatgar.garInfoPanelTitle", defaultContent.performance.hatgar.garInfoPanelTitle as string)}
        backdropStyle="frosted"
      >
        <div className="HatLegsTrackingTile__info-panel">
          <div className="HatLegsTrackingTile__info-panel-section">
            <h3 className="HatLegsTrackingTile__info-panel-title">
              {isChinaAbo
                ? t(
                    "foundersPlatinumLegsModal.chinaLabels.title",
                    String(hatContent["foundersPlatinumLegsModal"]["chinaLabels"]["title"]),
                    { ns: "hat" },
                  )
                : t("foundersPlatinumLegsModal.title", String(hatContent["foundersPlatinumLegsModal"]["title"]), {
                    ns: "hat",
                  })}
            </h3>
            <p className="HatLegsTrackingTile__info-panel-description">
              {isChinaAbo
                ? t(
                    "foundersPlatinumLegsModal.chinaLabels.description",
                    String(hatContent["foundersPlatinumLegsModal"]["chinaLabels"]["description"]),
                    { ns: "hat" },
                  )
                : t(
                    "foundersPlatinumLegsModal.description",
                    String(hatContent["foundersPlatinumLegsModal"]["description"]),
                    { ns: "hat" },
                  )}
            </p>
          </div>
          <div className="HatLegsTrackingTile__info-panel-section">
            <h3 className="HatLegsTrackingTile__info-panel-title">
              {isChinaAbo
                ? t(
                    "platinumLegsModal.chinaLabels.title",
                    String(hatContent["platinumLegsModal"]["chinaLabels"]["title"]),
                    { ns: "hat" },
                  )
                : t("platinumLegsModal.title", String(hatContent["platinumLegsModal"]["title"]), { ns: "hat" })}
            </h3>
            <p className="HatLegsTrackingTile__info-panel-description">
              {isChinaAbo
                ? t(
                    "platinumLegsModal.chinaLabels.description",
                    String(hatContent["platinumLegsModal"]["chinaLabels"]["description"]),
                    { ns: "hat" },
                  )
                : t("platinumLegsModal.description", String(hatContent["platinumLegsModal"]["description"]), {
                    ns: "hat",
                  })}
            </p>
          </div>
          <div className="HatLegsTrackingTile__info-panel-section">
            <h3 className="HatLegsTrackingTile__info-panel-title">
              {t("totalLegsModal.title", String(hatContent["totalLegsModal"]["title"]), { ns: "hat" })}
            </h3>
            <p className="HatLegsTrackingTile__info-panel-description">
              {isChinaAbo
                ? t(
                    "totalLegsModal.chinaLabels.description",
                    String(hatContent["totalLegsModal"]["chinaLabels"]["description"]),
                    { ns: "hat" },
                  )
                : t("totalLegsModal.description", String(hatContent["totalLegsModal"]["description"]), { ns: "hat" })}
            </p>
          </div>
          <div className="HatLegsTrackingTile__info-panel-section">
            <h3 className="HatLegsTrackingTile__info-panel-title">
              {t(
                "hatGarOverviewView.qualifictionCreditisNotCounting",
                String(hatContent["hatGarOverviewView"]["qualifictionCreditisNotCounting"]),
                { ns: "hat" },
              )}
            </h3>
            <p className="HatLegsTrackingTile__info-panel-description">
              {isChinaAbo
                ? t(
                    "modalQCNC.chinaLabels.creditsNotCounted",
                    String(hatContent["modalQCNC"]["chinaLabels"]["creditsNotCounted"]),
                    { ns: "hat" },
                  )
                : t("modalQCNC.creditsNotCounted", String(hatContent["modalQCNC"]["creditsNotCounted"]), {
                    ns: "hat",
                  })}
            </p>
          </div>
          {(faqDownload || brochureDownload) && (
            <>
              <div className="HatLegsTrackingTile__info-panel-section">
                <h3 className="HatLegsTrackingTile__info-panel-description">
                  {t(
                    "performance.hatgar.garInfoDownloadPrompt",
                    defaultContent.performance.hatgar.garInfoDownloadPrompt as string,
                  )}
                </h3>
                <div className="HatLegsTrackingTile__info-panel-downloads">
                  {faqDownload && (
                    <button
                      className="HatDownload"
                      onClick={() =>
                        handleDownload(
                          faqDownload,
                          t("hatGarOverviewView.faqFileName", String(hatContent["hatGarOverviewView"]["faqFileName"]), {
                            ns: "hat",
                          }),
                        )
                      }
                      aria-label={t(
                        "hatGarOverviewView.faqsAriaLabel",
                        String(hatContent["hatGarOverviewView"]["faqsAriaLabel"]),
                        { ns: "hat" },
                      )}
                      type="button"
                    >
                      <IconDownloadButton />
                      <span aria-hidden="true">
                        {t("hatGarOverviewView.faqs", String(hatContent["hatGarOverviewView"]["faqs"]), { ns: "hat" })}
                      </span>
                    </button>
                  )}
                  {!isChinaAbo && brochureDownload && (
                    <button
                      className="HatDownload"
                      onClick={() =>
                        handleDownload(
                          brochureDownload,
                          t(
                            "hatGarOverviewView.brochureFileName",
                            String(hatContent["hatGarOverviewView"]["brochureFileName"]),
                            { ns: "hat" },
                          ),
                        )
                      }
                      aria-label={t(
                        "hatGarOverviewView.brochure",
                        String(hatContent["hatGarOverviewView"]["brochure"]),
                        { ns: "hat" },
                      )}
                      type="button"
                    >
                      <IconDownloadButton />
                      <span aria-hidden="true">
                        {t("hatGarOverviewView.brochure", String(hatContent["hatGarOverviewView"]["brochure"]), {
                          ns: "hat",
                        })}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </LeafModal>
    </>
  );
};
